import { ENDPOINT } from "../../services/api-endpoint";
import { getList, getListById } from "../../services/services";

export type ManualOrderPartRow = {
  key: string;
  part: string;
  /** "—" when no template assigned (UI → "Not assigned") */
  workflow: string;
  /** `null` when not set on line (UI → "—") */
  qty: number | null;
  jobTags: string[];
  done: number;
  total: number;
  /** false → progress column shows "—" */
  hasQty: boolean;
};

export type ManualOrderJobRow = {
  key: string;
  name: string;
  status: "Open" | "In Progress" | "Draft";
  tasksDone: number;
  tasksTotal: number;
  recipe: string;
};

function unwrapList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: unknown[] }).data;
  }
  return [];
}

function mapMesJobStatus(s: string | null | undefined): "Open" | "In Progress" | "Draft" {
  const u = (s || "").toLowerCase();
  if (u.includes("progress")) return "In Progress";
  if (u.includes("draft")) return "Draft";
  return "Open";
}

/**
 * Loads manual order lines (orderparts) + part/workflow names + jobs for parts on this order.
 * Matches platform order-detail behaviour: template / jobs when present, else "—" + empty job tags.
 */
export async function fetchManualOrderDetailUi(orderId: number): Promise<{
  parts: ManualOrderPartRow[];
  jobs: ManualOrderJobRow[];
}> {
  const [orderPartsRaw, partsRaw, workflowsRaw, jobsRaw] = await Promise.all([
    getListById(ENDPOINT.ORDER_PARTS_LIST_BY_ORDER, orderId),
    getList(ENDPOINT.PARTS_LIST, { pagesize: 500, pagenumber: 0, serachtext: "" }),
    getList(ENDPOINT.WORKFLOWS_LIST, { pagesize: 500, pagenumber: 0, serachtext: "" }),
    getList(ENDPOINT.JOB_LIST, { pagesize: 500, pagenumber: 0, serachtext: "" }),
  ]);

  const orderPartRows = unwrapList(orderPartsRaw) as Record<string, unknown>[];
  const partItems = unwrapList(partsRaw) as { id?: number; partname?: string | null }[];
  const wfItems = unwrapList(workflowsRaw) as { id?: number; workflowname?: string | null }[];
  const jobItems = unwrapList(jobsRaw) as Record<string, unknown>[];

  const partNameById = new Map<string, string>();
  for (const p of partItems) {
    if (p.id != null) partNameById.set(String(p.id), (p.partname || "").trim() || `Part #${p.id}`);
  }

  const workflowNameById = new Map<string, string>();
  for (const w of wfItems) {
    if (w.id != null) {
      workflowNameById.set(
        String(w.id),
        (w.workflowname || "").trim() || `Workflow #${w.id}`,
      );
    }
  }

  const orderPartIds = new Set(
    orderPartRows.map((r) => (r.partid != null ? String(r.partid) : "")).filter(Boolean),
  );

  const jobsForOrder = jobItems.filter((j) => {
    const pid = j.partid != null ? String(j.partid) : "";
    return pid && orderPartIds.has(pid);
  });

  const jobTagsByPartId = new Map<string, string[]>();
  for (const j of jobsForOrder) {
    const pid = j.partid != null ? String(j.partid) : "";
    const name = String(j.jobname ?? j.jobName ?? "").trim();
    if (!pid || !name) continue;
    const list = jobTagsByPartId.get(pid) ?? [];
    if (!list.includes(name)) list.push(name);
    jobTagsByPartId.set(pid, list);
  }

  const parts: ManualOrderPartRow[] = orderPartRows.map((row, i) => {
    const pid = row.partid != null ? String(row.partid) : "";
    const wfId = row.workflowid != null ? String(row.workflowid) : "";
    const partLabel = pid ? partNameById.get(pid) || `Part #${pid}` : "—";

    let workflow = "—";
    if (wfId && wfId !== "0") {
      const wn = workflowNameById.get(wfId);
      workflow = wn && wn.trim() ? wn.trim() : "—";
    }

    const qtyStr = row.quantity != null ? String(row.quantity).trim() : "";
    const qtyNum = qtyStr === "" ? NaN : Number(qtyStr);
    const hasQty = Number.isFinite(qtyNum) && qtyNum >= 0;
    const qty: number | null = hasQty ? qtyNum : null;

    const produced = Number(row.quantity_produced ?? row.quantityProduced ?? 0);
    const done = Number.isFinite(produced) ? produced : 0;
    const total = hasQty ? Math.max(qty as number, 0) : 0;

    const tags = pid ? jobTagsByPartId.get(pid) ?? [] : [];

    return {
      key: `mo-${row.id ?? i}-${pid || i}`,
      part: partLabel,
      workflow,
      qty,
      jobTags: tags,
      done: hasQty ? Math.min(done, total) : 0,
      total: hasQty ? Math.max(total, 1) : 0,
      hasQty,
    };
  });

  const jobs: ManualOrderJobRow[] = jobsForOrder.map((j, i) => {
    const name = String(j.jobname ?? j.jobName ?? `Job ${i + 1}`).trim() || `Job ${i + 1}`;
    const td = Number(j.totalparts ?? j.totalParts ?? 0);
    const pd = Number(j.partproduced ?? j.partProduced ?? 0);
    return {
      key: `mj-${j.id ?? i}-${name}`,
      name,
      status: mapMesJobStatus(String(j.status ?? "")),
      tasksDone: Number.isFinite(pd) ? pd : 0,
      tasksTotal: Number.isFinite(td) && td > 0 ? td : Math.max(Number.isFinite(pd) ? pd : 0, 1),
      recipe: "—",
    };
  });

  return { parts, jobs };
}
